import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Role, User, UserSettings } from '../base/authentication/user';
import { CachingHttpClient } from './caching-http-client';
import { TranslateService } from '@ngx-translate/core';
import { parseLocaleOrUseDefault } from '../base/entities/locale';
import { catchError, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG } from '../config/app-config';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly appConfig = inject(APP_CONFIG);
  private readonly httpClient = inject(HttpClient);
  private readonly cachingHttpClient = inject(CachingHttpClient);
  private readonly translateService = inject(TranslateService);

  private readonly writableUser = signal<User | null>(null);
  private readonly writableSettings = signal<UserSettings | null>(null);

  public readonly user: Signal<User | null> = this.writableUser.asReadonly();
  public readonly settings: Signal<UserSettings | null> = this.writableSettings.asReadonly();
  public readonly isInitialized = computed(() => this.user() !== null && this.settings() !== null);
  public readonly isInitialized$ = toObservable(this.isInitialized);

  /**
   * Initializes the {@link User} and {@link Settings} information.
   * If the application is offline, the most recent response is returned instead.
   */
  initialize() {
    return this.cachingHttpClient.get<GetUserResponse>('api/GetUser').pipe(
      tap((response) => {
        this.writableUser.set(new User(response.userName, response.email, response.roles));
        const userSettings = response.settings;
        this.updateLocalSettings(userSettings);
      }),
      catchError((error) => {
        this.writableUser.set(null);
        throw error;
      })
    );
  }

  private updateLocalSettings(settings: UserSettings) {
    settings = {
      ...settings,
      // The below parsing ensures a supported language is always used,
      // for example if a 2 letter locale comes from backend via a default
      // setting or if the language value in the cache is outdated.
      language: settings.language ? parseLocaleOrUseDefault(settings.language) : null
    };

    this.writableSettings.set(settings);
    if (settings.language) {
      this.translateService.use(settings.language);
    }
  }

  updateSettings(settings: UserSettings) {
    this.updateLocalSettings(settings);
    return this.httpClient.put(`${this.appConfig.newApiUrl}api/UpsertUserSettings`, settings);
  }

  clear() {
    this.writableUser.set(null);
    this.writableSettings.set(null);
    this.cachingHttpClient.clear();
  }
}

export interface GetUserResponse {
  readonly userName: string;
  readonly email: string;
  readonly roles: Role[];
  readonly settings: UserSettings;
}
